import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, Plus } from 'lucide-react';
import { toast } from 'react-hot-toast';
import useOverlayHistory from 'services/useOverlayHistory';
import useScrollLock from 'services/useScrollLock';
import { getTripById, updateTrip } from '../../moduleServices/travel_services';
import {
  buildTripLogisticsPayload,
  withMappedTripLogistics,
} from './itineraryFormUtils';
import ItineraryDayTimeline from './ItineraryDayTimeline';
import CardStaySummary from './CardStaySummary';
import CardTransportSummary from './CardTransportSummary';
import ModalStayForm from './ModalStayForm';
import ModalTransportForm from './ModalTransportForm';
import TripOverviewPanel from './TripOverviewPanel';

const DRAWER_TABS = [
  { id: 'overview', label: 'Overview', needsTrip: false },
  { id: 'timeline', label: 'Timeline', needsTrip: true },
  { id: 'stays', label: 'Stays', needsTrip: true },
  { id: 'transports', label: 'Transports', needsTrip: true },
];

/**
 * Trip Details drawer — TripMade with nested transports / stays.
 *
 * entry: {
 *   intent?: 'view' | 'create-trip',
 *   trip?: object | null,
 * }
 */
const DrawerTripDetails = ({
  isOpen,
  onClose,
  entry,
  onTripsChanged,
}) => {
  const [shouldRender, setShouldRender] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [trip, setTrip] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [transportModal, setTransportModal] = useState(undefined);
  const [stayModal, setStayModal] = useState(undefined);
  const timeoutRef = useRef(null);

  const hasTrip = Boolean(trip?.id);
  const isCreateTrip = entry?.intent === 'create-trip' && !hasTrip;

  const handleRequestClose = useOverlayHistory(isOpen, onClose, {
    stateKey: 'travelTripDetailsDrawer',
  });

  const requestCloseGuarded = useCallback(() => {
    if (transportModal !== undefined || stayModal !== undefined) return;
    handleRequestClose();
  }, [transportModal, stayModal, handleRequestClose]);

  useScrollLock(isOpen);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key !== 'Escape' || isSubmitting) return;
      e.preventDefault();
      if (transportModal !== undefined) {
        setTransportModal(undefined);
        return;
      }
      if (stayModal !== undefined) {
        setStayModal(undefined);
        return;
      }
      handleRequestClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isSubmitting, transportModal, stayModal, handleRequestClose]);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (isOpen) {
      setShouldRender(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsVisible(true));
      });
    } else {
      setIsVisible(false);
      timeoutRef.current = setTimeout(() => {
        setShouldRender(false);
        timeoutRef.current = null;
      }, 300);
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [isOpen]);

  // Load trip when opening / entry changes
  useEffect(() => {
    if (!isOpen || !entry) return;

    let cancelled = false;

    const load = async () => {
      setActiveTab('overview');
      setTransportModal(undefined);
      setStayModal(undefined);

      if (entry.intent === 'create-trip') {
        setTrip(null);
        return;
      }

      const entryTrip = entry.trip ?? null;
      if (!entryTrip?.id) {
        setTrip(null);
        return;
      }

      setTrip(withMappedTripLogistics(entryTrip));
      try {
        const fresh = await getTripById(entryTrip.id);
        if (!cancelled) setTrip(withMappedTripLogistics(fresh));
      } catch (error) {
        console.error('Failed to refresh trip:', error);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // Intentional: reset from entry ids / intent, not full entry object identity
    // eslint-disable-next-line react-hooks/exhaustive-deps -- entry fields listed above
  }, [isOpen, entry?.intent, entry?.trip?.id]);

  // Logistics tabs require a saved trip
  useEffect(() => {
    const tab = DRAWER_TABS.find((t) => t.id === activeTab);
    if (tab?.needsTrip && !hasTrip) {
      setActiveTab('overview');
    }
  }, [activeTab, hasTrip]);

  const persistLogistics = async (nextTransports, nextStays) => {
    if (!trip?.id) return null;
    const logistics = buildTripLogisticsPayload({
      transports: nextTransports,
      stays: nextStays,
    });
    // PUT requires scalar trip fields; omit places so they are not replace-all'd
    const payload = {
      title: trip.title,
      description: trip.description || '',
      start_date: trip.start_date,
      end_date: trip.end_date,
      google_photos_url: trip.google_photos_url || '',
      ...logistics,
    };
    setIsSubmitting(true);
    try {
      const response = await updateTrip(trip.id, payload);
      const mapped = withMappedTripLogistics(response.data);
      setTrip(mapped);
      onTripsChanged?.(response.data);
      return mapped;
    } catch (error) {
      const msg =
        error.response?.data?.detail ||
        error.response?.data?.non_field_errors?.[0] ||
        error.message ||
        'Failed to save trip logistics';
      toast.error(typeof msg === 'string' ? msg : 'Failed to save trip logistics');
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  const upsertTransport = async (item) => {
    if (!trip?.id) return;
    const transports = trip.transports || [];
    const exists = transports.some((t) => t._tempId === item._tempId);
    const next = exists
      ? transports.map((t) => (t._tempId === item._tempId ? item : t))
      : [...transports, item];
    const saved = await persistLogistics(next, trip.stays || []);
    if (saved) {
      toast.success(exists ? 'Transport updated' : 'Transport added');
      setTransportModal(undefined);
    }
  };

  const removeTransport = async (item) => {
    if (!trip?.id) return;
    const next = (trip.transports || []).filter((t) => t._tempId !== item._tempId);
    const saved = await persistLogistics(next, trip.stays || []);
    if (saved) {
      toast.success('Transport removed');
      setTransportModal(undefined);
    }
  };

  const upsertStay = async (item) => {
    if (!trip?.id) return;
    const stays = trip.stays || [];
    const exists = stays.some((s) => s._tempId === item._tempId);
    const next = exists
      ? stays.map((s) => (s._tempId === item._tempId ? item : s))
      : [...stays, item];
    const saved = await persistLogistics(trip.transports || [], next);
    if (saved) {
      toast.success(exists ? 'Stay updated' : 'Stay added');
      setStayModal(undefined);
    }
  };

  const removeStay = async (item) => {
    if (!trip?.id) return;
    const next = (trip.stays || []).filter((s) => s._tempId !== item._tempId);
    const saved = await persistLogistics(trip.transports || [], next);
    if (saved) {
      toast.success('Stay removed');
      setStayModal(undefined);
    }
  };

  const handleTripSaved = (updated) => {
    setTrip(withMappedTripLogistics(updated));
    onTripsChanged?.(updated);
  };

  const handleTripDeleted = () => {
    setTrip(null);
    onTripsChanged?.();
  };

  if (!shouldRender) return null;

  const logisticsEnabled = hasTrip;
  const transports = trip?.transports || [];
  const stays = trip?.stays || [];

  return (
    <>
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-300 z-50 ${
          isVisible ? 'opacity-30' : 'opacity-0'
        }`}
        onClick={requestCloseGuarded}
      />

      <div
        className={`fixed top-0 right-0 h-full w-full md:w-[70%] max-w-[960px] bg-white shadow-xl z-[60] transform transition-transform duration-300 ease-in-out flex flex-col ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex-shrink-0 border-b border-gray-200">
          <div className="flex items-center px-2 py-2">
            <button
              type="button"
              onClick={handleRequestClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              title="Back"
            >
              <ChevronLeft className="h-5 w-5 text-gray-600" />
            </button>
            <div className="flex-1 text-center min-w-0 px-1">
              <h2 className="text-xl font-semibold text-gray-800 truncate">
                {isCreateTrip ? 'New Trip' : trip?.title || 'Trip Details'}
              </h2>
            </div>
            <div className="w-10 flex-shrink-0" />
          </div>

          <div className="flex items-stretch gap-3 px-4 -mb-px overflow-x-auto">
            {DRAWER_TABS.map(({ id, label, needsTrip }) => {
              const disabled = needsTrip && !logisticsEnabled;
              const selected = activeTab === id;
              return (
                <button
                  key={id}
                  type="button"
                  disabled={disabled}
                  onClick={() => !disabled && setActiveTab(id)}
                  className={`relative pb-2 text-sm font-semibold whitespace-nowrap transition-colors ${
                    disabled
                      ? 'text-gray-300 cursor-not-allowed'
                      : selected
                        ? 'text-gray-900'
                        : 'text-gray-400 hover:text-gray-600'
                  }`}
                  title={
                    disabled
                      ? 'Save the trip first to use this tab'
                      : undefined
                  }
                >
                  {label}
                  {selected && !disabled && (
                    <span className="absolute left-0 right-0 bottom-0 h-0.5 bg-module-travel rounded-full" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div
          className={`flex-1 min-h-0 px-4 py-3 ${
            activeTab === 'overview' ? 'overflow-hidden flex flex-col' : 'overflow-y-auto'
          }`}
        >
          {activeTab === 'overview' && (
            <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
              <TripOverviewPanel
                trip={
                  entry?.intent === 'create-trip' && !trip
                    ? null
                    : trip
                }
                onSaved={handleTripSaved}
                onDeleted={handleTripDeleted}
                onCancelCreate={handleRequestClose}
              />
            </div>
          )}

          {activeTab === 'timeline' && logisticsEnabled && (
            <ItineraryDayTimeline
              trip={trip}
              transports={transports}
              stays={stays}
              onOpenTransport={(item) => setTransportModal(item)}
              onOpenStay={(item) => setStayModal(item)}
            />
          )}

          {activeTab === 'stays' && logisticsEnabled && (
            <div className="space-y-2">
              <div className="flex justify-end mb-1">
                <button
                  type="button"
                  onClick={() => setStayModal(null)}
                  className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                >
                  <Plus className="h-3.5 w-3.5" /> Add stay
                </button>
              </div>
              {stays.length === 0 ? (
                <p className="text-sm text-gray-400 italic text-center py-8">No stays yet.</p>
              ) : (
                [...stays]
                  .sort((a, b) =>
                    String(a.checkin_date || '9999').localeCompare(String(b.checkin_date || '9999'))
                  )
                  .map((s) => (
                    <CardStaySummary
                      key={s._tempId}
                      stay={s}
                      onClick={() => setStayModal(s)}
                    />
                  ))
              )}
            </div>
          )}

          {activeTab === 'transports' && logisticsEnabled && (
            <div className="space-y-2">
              <div className="flex justify-end mb-1">
                <button
                  type="button"
                  onClick={() => setTransportModal(null)}
                  className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-sky-50 text-sky-800 hover:bg-sky-100"
                >
                  <Plus className="h-3.5 w-3.5" /> Add leg
                </button>
              </div>
              {transports.length === 0 ? (
                <p className="text-sm text-gray-400 italic text-center py-8">
                  No transport legs yet.
                </p>
              ) : (
                [...transports]
                  .sort((a, b) =>
                    String(a.departure_date || '9999').localeCompare(
                      String(b.departure_date || '9999')
                    )
                  )
                  .map((t) => (
                    <CardTransportSummary
                      key={t._tempId}
                      transport={t}
                      onClick={() => setTransportModal(t)}
                    />
                  ))
              )}
            </div>
          )}
        </div>
      </div>

      <ModalTransportForm
        isOpen={transportModal !== undefined}
        transport={transportModal}
        onClose={() => setTransportModal(undefined)}
        onSave={upsertTransport}
        onDelete={removeTransport}
        isSubmitting={isSubmitting}
      />

      <ModalStayForm
        isOpen={stayModal !== undefined}
        stay={stayModal}
        onClose={() => setStayModal(undefined)}
        onSave={upsertStay}
        onDelete={removeStay}
        isSubmitting={isSubmitting}
      />
    </>
  );
};

export default DrawerTripDetails;
